/* eslint-disable import/no-extraneous-dependencies */
import moment from 'moment'

// PD-0007 Fase 5 - rotulos e formatadores da Central de Resultados.
//
// O SEM_DADO abaixo e o coracao do modulo: toda funcao devolve o traco quando o valor e `null`,
// e nenhuma delas aceita cair em zero. Se alguem trocar por `?? 0` "para simplificar", a tela
// passa a afirmar "0%" onde a Central nao sabe - o unico defeito que este modulo nao pode ter.
export const SEM_DADO = '—'

/** Frase fixa do cabecalho. A Central mede correlacao no tempo, nunca causalidade. */
export const AVISO_CAUSALIDADE =
  'Estes números descrevem o que aconteceu na carteira. Nenhum deles afirma que o resultado foi causado pelo Cartivia.'

/** Rotulo obrigatorio da receita: o ERP nao envia o que abateria dela. */
export const AVISO_RECEITA =
  'Valor bruto de item (quantidade × valor unitário). Não considera devolução, desconto, frete ou impostos, que o ERP não envia.'

/** Rotulo obrigatorio: o proponente pediu ticket medio, e ticket medio nao existe neste dado. */
export const AVISO_VALOR_MEDIO =
  'Não é ticket médio — o ERP não envia número de pedido/nota. É a receita do mês dividida pelos clientes que compraram.'

export const AVISO_RECEITA_RASTREADA =
  'Parcela da receita em que a compra havia sido prevista e o vendedor trabalhou o cliente. Indica rastro, não causa.'

export const AVISO_INDETERMINADO =
  'Clientes sem histórico suficiente para classificar. Ficam fora do numerador e do denominador de toda taxa — nunca são contados como "não compraram".'

export const formatDataHora = (valor: string | null): string => {
  if (!valor) return SEM_DADO
  const m = moment(valor, 'YYYY-MM-DD HH:mm:ss')
  return m.isValid() ? m.format('DD/MM/YYYY HH:mm') : SEM_DADO
}

export const formatData = (valor: string | null): string => {
  if (!valor) return SEM_DADO
  const m = moment(valor, 'YYYY-MM-DD')
  return m.isValid() ? m.format('DD/MM/YYYY') : SEM_DADO
}

const MESES_ABREVIADOS = [
  'jan',
  'fev',
  'mar',
  'abr',
  'mai',
  'jun',
  'jul',
  'ago',
  'set',
  'out',
  'nov',
  'dez',
]

/**
 * 'aaaa-MM' -> 'ago/2026'. Competencia e mes-calendario: nunca exibir dia.
 *
 * Tabela fixa em vez de `moment().format('MMM/YYYY')`: o locale do moment neste app e o padrao
 * (ingles), entao o format devolvia "Aug/2026" - visto em runtime. Chamar `moment.locale('pt-br')`
 * resolveria aqui, mas e estado GLOBAL: mudaria a saida de toda tela que usa moment, muito alem
 * desta feature. O app trata locale por componente (o Calendar configura o dayjs dele), nunca
 * globalmente.
 */
export const formatCompetencia = (valor: string | null): string => {
  if (!valor) return SEM_DADO
  const partes = /^(\d{4})-(\d{2})/.exec(valor)
  if (!partes) return SEM_DADO
  const mes = Number(partes[2])
  if (mes < 1 || mes > 12) return SEM_DADO
  return `${MESES_ABREVIADOS[mes - 1]}/${partes[1]}`
}

export const formatMoeda = (valor: number | null): string => {
  if (valor === null || valor === undefined) return SEM_DADO
  return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

export const formatNumero = (valor: number | null): string => {
  if (valor === null || valor === undefined) return SEM_DADO
  return valor.toLocaleString('pt-BR')
}

export const formatDecimal = (valor: number | null): string => {
  if (valor === null || valor === undefined) return SEM_DADO
  return valor.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

/**
 * A API ja devolve o percentual em 0..100 (nao em fracao) e com 1 casa.
 * ⚠️ Diferente do `formatTaxaFalha` do PD-0003, que recebe fracao 0..1 e multiplica por 100.
 */
export const formatPercentual = (valor: number | null): string => {
  if (valor === null || valor === undefined) return SEM_DADO
  return `${valor.toFixed(1).replace('.', ',')}%`
}

/** Variacao ganha sinal explicito: "+3,2%" le diferente de "3,2%". */
export const formatVariacao = (
  valor: number | null,
  sufixo: string
): string => {
  if (valor === null || valor === undefined) return SEM_DADO
  const sinal = valor > 0 ? '+' : ''
  return `${sinal}${valor.toFixed(1).replace('.', ',')}${sufixo}`
}

export const rotuloFase: Record<FaseTenant, string> = {
  BASE_DE_TESTE: 'Base de teste',
  DEMONSTRACAO: 'Demonstração',
  EM_IMPLANTACAO: 'Em implantação',
  EM_FUNCIONAMENTO: 'Em funcionamento',
  STANDBY: 'Standby',
  INATIVO: 'Inativo',
}

export const FASES: FaseTenant[] = [
  'BASE_DE_TESTE',
  'DEMONSTRACAO',
  'EM_IMPLANTACAO',
  'EM_FUNCIONAMENTO',
  'STANDBY',
  'INATIVO',
]

/**
 * Por que a positivacao nao pode ser exibida. Devolve `null` quando ela PODE.
 *
 * Sao tres causas distintas e a tela precisa dizer qual: um traco mudo faz o super-admin
 * concluir "esta quebrado" quando o certo e "ainda nao da para afirmar" ou "o dado nao chegou".
 */
export const motivoSemPositivacao = (
  competencia: Pick<
    CarteiraCompetenciaType,
    | 'positivacaoPercentual'
    | 'lastroInsuficiente'
    | 'semMovimentoNaCompetencia'
    | 'semVendaNaCompetencia'
  >
): string | null => {
  if (competencia.positivacaoPercentual !== null) return null
  // Ordem importa: "sem movimento" e o caso ACUSATORIO e vem primeiro; "sem venda" sozinho e o
  // mes em curso antes da primeira venda entrar, que nao acusa nada.
  if (competencia.semMovimentoNaCompetencia) {
    return 'Sem movimento no período — nenhuma venda chegou neste mês. Verifique a integração do cliente antes de ler como queda.'
  }
  if (competencia.semVendaNaCompetencia) {
    return 'Nenhuma venda registrada até agora nesta competência. O mês ainda está em curso — não há taxa a calcular.'
  }
  if (competencia.lastroInsuficiente) {
    return 'Sem base para afirmar ausência de compra: a Central ainda não tinha 90 dias de observação própria nesta competência. A taxa daria 100% por construção.'
  }
  return 'Não há clientes elegíveis nesta competência (todos ficaram indeterminados).'
}

/**
 * Rotulo CURTO do badge que substitui a taxa suprimida. `null` quando a taxa pode ser exibida.
 *
 * Espelha as tres causas de {@link motivoSemPositivacao}, que fica no tooltip. Sao rotulos
 * diferentes de proposito: com dois rotulos para tres causas, o mes em curso sem venda aparecia
 * como "Sem base", que e outra coisa - e a unica das tres que NAO acusa nada.
 */
export const rotuloSemPositivacao = (
  competencia: Pick<
    CarteiraCompetenciaType,
    | 'positivacaoPercentual'
    | 'lastroInsuficiente'
    | 'semMovimentoNaCompetencia'
    | 'semVendaNaCompetencia'
  >
): string | null => {
  if (competencia.positivacaoPercentual !== null) return null
  if (competencia.semMovimentoNaCompetencia) return 'Sem movimento'
  if (competencia.semVendaNaCompetencia) return 'Sem venda ainda'
  if (competencia.lastroInsuficiente) return 'Sem base'
  return 'Sem elegíveis'
}

/** Primeiro dia do mes corrente em 'aaaa-MM' - mesma nocao de "mes em curso" do backend. */
export const competenciaCorrente = (): string => moment().format('YYYY-MM')

/**
 * Rotulo curto de cobertura da competencia.
 *
 * ⚠️ "Mês em curso" e "ainda não fechada" NAO sao a mesma coisa, e tratar as duas juntas foi um
 * defeito visto em runtime: o fechamento acontece em D+8 (janela +-7d da reconciliacao), entao no
 * dia 2 de setembro a competencia de AGOSTO ainda esta aberta - e vinha rotulada "mês em curso",
 * que e falso e faz o leitor descontar um mes inteiro de receita real.
 */
export const rotuloCobertura = (
  competencia: Pick<
    CarteiraCompetenciaType,
    'competencia' | 'fechada' | 'coberturaParcial'
  >
): string | null => {
  const emCurso = competencia.competencia === competenciaCorrente()
  if (emCurso) {
    return 'Mês em curso — parcial'
  }
  if (!competencia.fechada) {
    return competencia.coberturaParcial
      ? 'Ainda não fechada — cobertura parcial'
      : 'Ainda não fechada'
  }
  if (competencia.coberturaParcial) {
    return 'Fechada com cobertura parcial'
  }
  return null
}

/**
 * Marca PERMANENTE de reabertura (Fase 7).
 *
 * ⚠️ Separada de `rotuloCobertura` de proposito: cobertura descreve o DADO (que janela o ERP
 * entregou), reabertura descreve uma ACAO HUMANA sobre dado ja fechado. Fundir as duas numa frase
 * so faria a segunda desaparecer quando a primeira tivesse algo a dizer - e e justamente a que nao
 * pode sumir. Por isso ela nao desaparece nunca: competencia reaberta e competencia em que alguem
 * mexeu depois de fechada, e quem le o numero precisa saber disso sem perguntar.
 */
export const rotuloReabertura = (
  competencia: Pick<
    CarteiraCompetenciaType,
    'reaberturas' | 'reabertoEm' | 'reabertoPor'
  >
): string | null => {
  const vezes = competencia.reaberturas ?? 0
  if (vezes < 1) {
    return null
  }
  const quando = competencia.reabertoEm
    ? ` em ${formatDataHora(competencia.reabertoEm)}`
    : ''
  // Autor pode faltar (competencia reaberta antes de a coluna existir). Omitir o trecho e melhor
  // que escrever "por -": a marca continua verdadeira sem ele.
  const quem = competencia.reabertoPor ? ` por ${competencia.reabertoPor}` : ''
  const plural = vezes > 1 ? `${vezes}× ` : ''
  return `Reaberta ${plural}${quando}${quem}`.replace(/\s+/g, ' ').trim()
}

/**
 * Opcoes do seletor de competencia, do mes corrente para tras.
 *
 * Geradas no cliente de proposito: nao ha rota que liste competencias, e criar uma so para o
 * seletor seria endpoint novo para um problema de tela. Escolher um mes sem dado devolve as linhas
 * com `semDadoNaCompetencia`, que a tela ja precisa saber renderizar de qualquer forma.
 */
export const competenciasDisponiveis = (quantidade: number): string[] => {
  const base = moment().startOf('month')
  const lista: string[] = []
  for (let i = 0; i < quantidade; i += 1) {
    lista.push(base.clone().subtract(i, 'months').format('YYYY-MM'))
  }
  return lista
}
